/**
 * Legislation section entry (§2.7): /dashboard/legislation (list, screenshots/25, 31–34) and
 * /dashboard/legislation/:recordId (detail, 26, 28, 35–40). bootLegislation() runs before the first
 * render (main.tsx) and hydrates the stores from the snapshots the Worker inlines
 * (snapshot:legislation, snapshot:legislation:{recordId}), so the first frame has real data.
 */
import { useState } from "react";
import { loadAsPage, navigate } from "../app/router";
import { connectionStore } from "../stores/connectionStore";
import { can, type OrgRole, type UserGroup } from "../nav/navConfig";
import { LegislationDetail } from "./LegislationDetail";
import { LegislationListPage } from "./LegislationList";
import { ForecastMethod } from "../pages/ForecastMethod";
import { BriefView } from "../briefs/BriefView";
import type { Brief } from "../briefs/brief";
import { DETAIL_TABS, createLegislationStore, type DetailTab, type LegislationStore } from "./legislationStore";
import { LIST_TABS, listStore, type ListTab } from "./listStore";
import type { LegislationDetail as Detail, ListData } from "./types";

let detailStore: LegislationStore | null = null;
let bootedRecord: string | null = null;
let methodAccuracy: ListData["accuracy"] | null = null;
let bootedBrief: Brief | null = null;

/** The open record's store (team positions live, realtime/teamLive.ts). */
export const legislationDetailStore = () => detailStore;

const recordOf = (path: string) => {
  const m = path.match(/^\/dashboard\/legislation\/([^/]+)/);
  return m ? decodeURIComponent(m[1]) : null;
};

export function bootLegislation(path: string, inlined: <T>(id: string) => T | null, search = location.search) {
  const record = recordOf(path);
  if (record === "forecast-method" || /^\/dashboard\/forecast-method/.test(path)) {
    methodAccuracy = inlined<{ accuracy: ListData["accuracy"] }>("snapshot:forecast-method")?.accuracy ?? null;
    return;
  }
  const tab = new URLSearchParams(search).get("tab");
  if (record && record !== "forecast-method") {
    const snap = inlined<{ seq?: number; at?: string; detail: Detail }>(`snapshot:legislation:${record}`);
    if (!snap?.detail) return;
    detailStore = createLegislationStore();
    detailStore.getState().hydrate(snap.detail, snap.seq ?? 0);
    if (tab && (DETAIL_TABS as string[]).includes(tab)) detailStore.getState().setTab(tab as DetailTab);
    bootedRecord = record;
    bootedBrief = inlined<Brief>(`snapshot:brief:${record}`);
    connectionStore.setState({ status: "snapshot", snapshotAt: snap.at ?? null });
    return;
  }
  const list = inlined<ListData>("snapshot:legislation");
  if (list) {
    listStore.getState().hydrate(list);
    connectionStore.setState({ status: "snapshot", snapshotAt: list.at });
  }
}

export function LegislationPage({ group, role, path }: { group: UserGroup; role: OrgRole | null; path: string }) {
  const record = recordOf(path);
  const [tab, setTab] = useState<ListTab>(() => {
    const t = new URLSearchParams(location.search).get("tab");
    return t && (LIST_TABS as string[]).includes(t) ? (t as ListTab) : "vote";
  });
  if (record === "forecast-method" || /^\/dashboard\/forecast-method/.test(path)) return <ForecastMethod accuracy={methodAccuracy ?? listStore.getState().data?.accuracy ?? null} />;
  if (record) {
    // Navigation loads a record's snapshot before it renders (main.tsx setPreparer); otherwise load it as a page.
    if (record !== bootedRecord) return loadAsPage();
    if (!detailStore) return <p className="empty-panel">This item isn't available yet.</p>;
    if (/\/brief\/?$/.test(path) && can(group, role, "briefMe")) return <BriefView detail={detailStore.getState().detail!} initial={bootedBrief} role={role} />;
    return <LegislationDetail key={record} store={detailStore} group={group} role={role} />;
  }
  if (!listStore.getState().data) listStore.getState().prefetch(); // e.g. its snapshot failed: the list fills in when this answers
  return (
    <LegislationListPage group={group} role={role} tab={tab} onTab={(t) => {
      setTab(t);
      navigate(`/dashboard/legislation${t === "vote" ? "" : `?tab=${t}`}`, { replace: true });
    }} />
  );
}
